import "@/App.css";
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/caesar";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import WarRoom from "@/pages/WarRoom";
import Today from "@/pages/Today";
import VivianCommand from "@/pages/VivianCommand";
import Projects from "@/pages/Projects";
import People from "@/pages/People";
import Money from "@/pages/Money";
import CalendarPage from "@/pages/CalendarPage";
import Intelligence from "@/pages/Intelligence";
import Alerts from "@/pages/Alerts";
import MorningBrief from "@/pages/MorningBrief";
import Connections from "@/pages/Connections";
import EOD from "@/pages/EOD";
import ChiefOfStaff from "@/pages/ChiefOfStaff";
import Websites from "@/pages/Websites";
import Social from "@/pages/Social";
import SocialActivity from "@/pages/SocialActivity";
import Admin from "@/pages/Admin";
import MemberPortal from "@/pages/MemberPortal";
import { Loading } from "@/components/common";

function Home() {
  const { user } = useAuth();
  if (user?.role === "member_investor") return <Navigate to="/portal" replace />;
  if (user?.role === "team_member") return <Navigate to="/vivian" replace />;
  return <WarRoom />;
}

function ChangePasswordGate() {
  const { checkAuth, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("New password must be at least 8 characters");
    if (pw !== pw2) return setErr("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: pw }); toast.success("Password updated"); await checkAuth(); }
    catch (e2) { setErr(e2.response?.data?.detail || "Could not update password"); } finally { setBusy(false); }
  };
  const inputCls = "w-full h-12 rounded-lg bg-surface border border-edge px-4 outline-none focus:border-gold/50";
  return (
    <div className="min-h-screen bg-ink text-[#FAFAFA] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm animate-fade-up space-y-3" data-testid="force-change-password">
        <img src="/szr-logo.png" alt="SZR" className="mx-auto w-28 h-28 object-contain" />
        <h1 className="font-display text-2xl font-extrabold text-center">Change Your Password</h1>
        <p className="text-center text-xs text-[#A3A3A3] mb-2">You must set a new password before continuing.</p>
        <input data-testid="cp-current" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className={inputCls} />
        <input data-testid="cp-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className={inputCls} />
        <input data-testid="cp-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className={inputCls} />
        {err && <p data-testid="cp-error" className="text-sred text-sm">{err}</p>}
        <button data-testid="cp-submit" disabled={busy} type="submit" className="w-full h-12 rounded-lg bg-gold text-ink font-bold disabled:opacity-60">{busy ? "…" : "UPDATE PASSWORD"}</button>
        <button type="button" onClick={logout} className="w-full text-xs text-[#A3A3A3] hover:text-sred">Log out</button>
      </form>
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center bg-ink"><Loading label="Securing session…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset) return <ChangePasswordGate />;
  if (user.role === "member_investor" && location.pathname !== "/portal") return <Navigate to="/portal" replace />;
  if (location.pathname === "/admin" && user.role !== "super_admin") return <Navigate to="/" replace />;
  return <Layout><Outlet /></Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Home />} />
        <Route path="/portal" element={<MemberPortal />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/chief" element={<ChiefOfStaff />} />
        <Route path="/today" element={<Today />} />
        <Route path="/vivian" element={<VivianCommand />} />
        <Route path="/eod" element={<EOD />} />
        <Route path="/websites" element={<Websites />} />
        <Route path="/social" element={<Social />} />
        <Route path="/social-report" element={<SocialActivity />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/people" element={<People />} />
        <Route path="/money" element={<Money />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/intelligence" element={<Intelligence />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/brief" element={<MorningBrief />} />
        <Route path="/connections" element={<Connections />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#141414", border: "1px solid #262626", color: "#FAFAFA" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
